<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <v-row align="center">
          <v-col cols="12" md="6">
            <div class="label mb-2">Name</div>
            <v-text-field
              v-model="form.name"
              :error="form.errors.name"
              :error-messages="form.errors.name"
              :rules="[validationRules.required]"
              solo
              flat
              outlined
              dense
              placeholder="Holiday name"
            />
          </v-col>
          <v-col cols="12" md="6">
            <div class="label mb-2">Name (Chinese)</div>
            <v-text-field
              v-model="form.name_cn"
              solo
              flat
              outlined
              dense
              placeholder="Holiday name (chinese)"
            />
          </v-col>
          <v-col cols="12" md="4">
            <div class="label mb-2">Holiday Date</div>
            <v-menu
              ref="dateMenu"
              v-model="dateMenu"
              :return-value.sync="dates"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="dateRangeText"
                  :rules="[validationRules.required]"
                  :error="hasFormDatesError"
                  :error-messages="formDatesErrorMessage"
                  placeholder="Select holiday date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dates" range scrollable>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dateMenu = false">
                  Cancel
                </v-btn>
                <v-btn text color="primary" @click="$refs.dateMenu.save(dates)">
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="form.is_annual"
              inset
              persistent-hint
              hint="hint"
            >
              <template #label>
                Libur Tahunan ? {{ yesOrNoText(form.is_annual) }}
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-icon v-bind="attrs" right small v-on="on">
                      mdi-help-circle
                    </v-icon>
                  </template>
                  <span>
                    Hari libur yang jatuh pada tanggal yang sama di setiap
                    tahunnya
                  </span>
                </v-tooltip>
              </template>
              <template #message>
                Hari libur yang jatuh pada
                <strong>tanggal yang sama di setiap tahunnya</strong>
              </template>
            </v-switch>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="form.is_global"
              inset
              persistent-hint
              hint="hint"
            >
              <template #label>
                Libur Nasional ? {{ yesOrNoText(form.is_global) }}
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-icon v-bind="attrs" right small v-on="on">
                      mdi-help-circle
                    </v-icon>
                  </template>
                  <span>
                    Hari libur nasional yang berlaku untuk semua kelas
                  </span>
                </v-tooltip>
              </template>
              <template #message>
                Hari libur nasional yang
                <strong>berlaku untuk semua kelas</strong>
              </template>
            </v-switch>
          </v-col>
          <v-col v-show="!form.is_global" cols="12">
            <div class="label mb-2">Classrooms</div>
            <v-autocomplete
              v-model="form.classroom_ids"
              :items="classrooms"
              :error="form.errors && form.errors.classroom_ids"
              :error-messages="form.errors.classroom_ids"
              :loading="isClassroomListLoading"
              :search-input.sync="searchInputClassroom"
              item-text="name"
              item-value="id"
              solo
              flat
              outlined
              dense
              placeholder="Choose class"
              cache-items
              multiple
              chips
            />
          </v-col>
          <v-col cols="12">
            <div class="label mb-2">Status</div>
            <v-select
              v-model="form.status"
              :items="statusOptions"
              :rules="[validationRules.required]"
              :error="form.errors.status"
              :error-messages="form.errors.status"
              solo
              flat
              outlined
              dense
              placeholder="Select holiday status"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import constant from '@/constant'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'HolidayForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    holiday: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      dateMenu: false,
      form: this.$inertia.form({
        name: null,
        name_cn: null,
        start_date: null,
        end_date: null,
        is_global: null,
        is_annual: null,
        status: null,
        classroom_ids: [],
      }),
      dates: [],
      searchInputClassroom: null,
    }
  },
  computed: {
    ...mapGetters({
      classroomList: 'classroom/searchList',
      isClassroomListLoading: 'classroom/isSearchListLoading',
    }),
    dateRangeText() {
      return this.dates.join(' - ')
    },
    hasFormDatesError() {
      return (
        this.form.errors &&
        (this.form.errors.start_date || this.form.errors.end_date)
      )
    },
    formDatesErrorMessage() {
      if (this.hasFormDatesError) {
        return 'Date is required'
      }
      return null
    },
    isReadonly() {
      return this.mode === 'view'
    },
    statusOptions() {
      return constant.holidayStatusOptions
    },
    classrooms() {
      return this.classroomList.data
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
    searchInputClassroom(val) {
      if (val) {
        this.debouncedSearchClassroomList()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchClassroomList = _.debounce(
      this.searchClassroomList,
      500
    )
  },
  methods: {
    ...mapActions({
      getClassroomList: 'classroom/search',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.holiday) {
        console.error(
          `holiday props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async init() {
      if (this.mode != 'add' && this.holiday != null) {
        _.merge(this.form, this.holiday)
        this.dates = [this.form.start_date, this.form.end_date]
        if (this.holiday.classrooms && this.holiday.classrooms.length > 0) {
          for (const classroom of this.holiday.classrooms) {
            this.form.classroom_ids.push(classroom.id)
          }
          await this.getClassroomList({
            search: null,
            filters: {
              id: this.form.classroom_ids,
            },
          })
        }
      }
    },
    async searchClassroomList() {
      await this.getClassroomList({
        search: this.searchInputClassroom,
        filters: {
          status: 'ACTIVE',
        },
      })
    },
    cancel() {
      this.$emit('cancel')
    },
    async submit() {
      if (this.$refs.form.validate()) {
        if (this.dates.length === 2) {
          this.form.start_date = this.dates[0]
          this.form.end_date = this.dates[1]
        }
        if (this.mode === 'add') {
          this.form.post('/holidays')
        } else if (this.mode === 'edit') {
          this.form.put(`/holidays/${this.holiday.id}`)
        }
      }
    },
  },
}
</script>
